import React from 'react';
import { create } from 'zustand';

import { EMessageType, IMessage, IReceiverMessageData } from '@src/types';

interface INotification {
  id?: 'newVote' | 'newPoll' | 'meeting-end-soon';
  text: string;
  title?: string;
  peerId?: string;
  source?:
    | 'polls'
    | 'raised-hand'
    | 'recording'
    | 'user-type'
    | 'share-screen'
    | 'settings'
    | 'mute'
    | 'question-and-answer'
    | 'meeting-end-soon'
    | 'participants'
    | 'annotation'
    | 'calculator'
    | 'connection-issue';
  requestId?: string;
  icon?: React.ReactNode;
}

// Persistent notification that doesn't auto-dismiss
interface IPersistentNotification {
  id: string;
  text: string;
  title: string;
  type: 'connection-issue';
  icon?: React.ReactNode;
}

interface INotificationStore {
  notifications: INotification[];
  messagesNotifications: Map<string, IMessage[]>;
  persistentNotification: IPersistentNotification | null;
  setNotifications: (notifications: INotification[]) => void;
  resetNotifications: () => void;
  appendNotification: (notification: INotification) => void;
  addRaisedHandNotification: (peerId: string, peerName: string) => void;
  setMessagesNotifications: ({
    peer,
    messages,
    isDrawerOpen,
    selectedKey,
    isReplace,
    selectedReceiver,
    isPipActive,
  }: {
    peer: string;
    messages: IMessage[];
    messageType: EMessageType;
    isDrawerOpen: boolean;
    selectedKey: string;
    isReplace?: boolean;
    selectedReceiver: IReceiverMessageData;
    isPipActive: boolean;
  }) => void;
  removeNotifications: (peer: string, messageId: string) => void;
  removeNotificationByRequestId: (requestId: string) => void;
  markAllAsShown: (keysMap: { [key: string]: boolean }) => void;
  setPersistentNotification: (notification: IPersistentNotification | null) => void;
  clearPersistentNotification: () => void;
}

export const useNotificationStore = create<INotificationStore>((set) => ({
  messagesNotifications: new Map(),
  notifications: [],
  persistentNotification: null,
  setNotifications: (notifications) => set({ notifications }),
  resetNotifications: () =>
    set((state) => {
      const withRequestId = state.notifications.filter((n) => !!n.requestId);
      // Only update if we're actually removing something
      if (withRequestId.length === state.notifications.length) {
        return state; // No change needed
      }
      return { notifications: withRequestId };
    }),
  appendNotification: (notification) =>
    set((state) => ({ ...state, notifications: [...state.notifications, notification] })),
  setPersistentNotification: (notification) => set({ persistentNotification: notification }),
  clearPersistentNotification: () => set({ persistentNotification: null }),
  //  Add new notifications (replace old ones from the same peer)
  setMessagesNotifications: ({
    peer,
    messages,
    messageType,
    isDrawerOpen,
    selectedKey,
    isReplace,
    selectedReceiver,
    isPipActive,
  }: {
    peer: string;
    messages: IMessage[];
    messageType: EMessageType;
    isDrawerOpen: boolean;
    selectedKey: string;
    isReplace?: boolean;
    selectedReceiver: IReceiverMessageData;
    isPipActive: boolean;
  }) =>
    set((state) => {
      const newMessageNotifications = new Map(state.messagesNotifications);

      // clear if chat drawer open and current tab is chat and matching receiver
      if (
        isDrawerOpen &&
        !isPipActive &&
        selectedKey === 'chat' &&
        messageType === selectedReceiver.key &&
        (messageType === EMessageType.PRIVATE ? peer === selectedReceiver.value : true)
      ) {
        newMessageNotifications.set(peer, []);
      } else {
        newMessageNotifications.set(
          peer,
          isReplace ? messages : [...(state.messagesNotifications.get(peer) || []), ...messages]
        );
      }

      return {
        messagesNotifications: newMessageNotifications,
      };
    }),

  removeNotifications: (peer: string, messageId?: string) =>
    set((state) => {
      const newMessageNotifications = new Map(state.messagesNotifications);
      if (!newMessageNotifications.has(peer)) {
        return state;
      }

      if (messageId) {
        const updatedMessages =
          newMessageNotifications.get(peer)?.filter((msg) => msg.chatMessageId !== messageId) || [];

        if (updatedMessages.length > 0) {
          newMessageNotifications.set(peer, updatedMessages);
        } else {
          newMessageNotifications.delete(peer);
        }
      } else {
        newMessageNotifications.delete(peer);
      }

      return {
        messagesNotifications: newMessageNotifications,
      };
    }),

  removeNotificationByRequestId: (requestId: string) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.requestId !== requestId),
    })),

  markAllAsShown: (keysMap) =>
    set((state) => {
      const newMessageNotifications = new Map(state.messagesNotifications);
      [...state.messagesNotifications.entries()].forEach(([key, messages]) => {
        newMessageNotifications.set(
          key,
          messages?.map((msg) => ({ ...msg, isShown: msg.isShown || keysMap[msg.chatMessageId || ''] })) || []
        );
      });
      return { messagesNotifications: newMessageNotifications };
    }),

  //adding raise hands notifications to the array of notifications
  addRaisedHandNotification: (peerId, peerName) =>
    set((state) => ({
      notifications: [
        ...state.notifications,
        {
          peerId,
          text: 'raised a hand',
          title: peerName,
          source: 'raised-hand',
        },
      ],
    })),
}));
